/**
 * BYK-4: GLB Model Loading Service
 *
 * Provides async GLB model loading with caching for efficient 3D rendering.
 * Features:
 * - Async model loading from R2 storage via API
 * - Template mesh caching (one load per product)
 * - Instance cloning for multiple placements
 * - Loading state management
 * - Error handling with fallback to box meshes
 */

import * as BABYLON from "@babylonjs/core";
import "@babylonjs/loaders/glTF"; // Enable GLTF/GLB loader

// =============================================================================
// Constants
// =============================================================================

const CM_TO_M = 0.01;

// API base URL (from Vite env)
const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4205";

// =============================================================================
// Types
// =============================================================================

interface LoadedModel {
  /** Root mesh containing all loaded meshes */
  rootMesh: BABYLON.Mesh;
  /** All meshes in the model */
  meshes: BABYLON.AbstractMesh[];
  /** Bounding info for sizing */
  boundingInfo: BABYLON.BoundingInfo;
}

interface CachedTemplate {
  /** The loaded model template */
  model: LoadedModel;
  /** Timestamp when loaded */
  loadedAt: number;
}

type LoadingState = "idle" | "loading" | "loaded" | "error";

interface ModelLoadingStatus {
  state: LoadingState;
  error?: string;
}

// =============================================================================
// Cache & State (Module-level)
// =============================================================================

/** Cache of loaded GLB templates keyed by glbKey */
const templateCache = new Map<string, CachedTemplate>();

/** Loading status for each model */
const loadingStatus = new Map<string, ModelLoadingStatus>();

/** Pending load promises to avoid duplicate requests */
const pendingLoads = new Map<string, Promise<LoadedModel | null>>();

// =============================================================================
// Public API
// =============================================================================

/**
 * Check if a model is already loaded
 */
export function isModelLoaded(glbKey: string): boolean {
  return templateCache.has(glbKey);
}

/**
 * Get the loading status for a model
 */
export function getModelLoadingStatus(glbKey: string): ModelLoadingStatus {
  return loadingStatus.get(glbKey) || { state: "idle" };
}

/**
 * Load a GLB model from R2 storage.
 * Returns cached template if already loaded.
 * Returns null if loading fails.
 */
export async function loadGlbModel(
  glbKey: string,
  scene: BABYLON.Scene
): Promise<LoadedModel | null> {
  // Return cached template if available
  if (templateCache.has(glbKey)) {
    return templateCache.get(glbKey)!.model;
  }

  // If already loading, wait for the pending promise
  if (pendingLoads.has(glbKey)) {
    return pendingLoads.get(glbKey)!;
  }

  // Start loading
  const loadPromise = loadModelInternal(glbKey, scene);
  pendingLoads.set(glbKey, loadPromise);

  try {
    const result = await loadPromise;
    return result;
  } finally {
    pendingLoads.delete(glbKey);
  }
}

/**
 * Create an instance of a loaded model for a specific volume.
 * The model must be loaded first via loadGlbModel.
 * Returns null if template not found.
 */
export function createModelInstance(
  glbKey: string,
  volumeId: string,
  position: { x: number; y: number; z: number },
  rotation: number,
  targetDimensions: { width: number; height: number; depth: number },
  scene: BABYLON.Scene
): BABYLON.TransformNode | null {
  const cached = templateCache.get(glbKey);
  if (!cached) {
    console.warn(`[GLB Loader] Template not loaded for key: ${glbKey}`);
    return null;
  }

  const { model } = cached;

  // Create a root transform node for this instance
  const instanceRoot = new BABYLON.TransformNode(`glb-instance-${volumeId}`, scene);

  // Clone all meshes
  for (const mesh of model.meshes) {
    if (mesh instanceof BABYLON.Mesh) {
      const clone = mesh.clone(`${mesh.name}-${volumeId}`, instanceRoot);
      if (clone) {
        clone.setEnabled(true);
        clone.isPickable = true;
        // Meshes are parented to instanceRoot, positioned relative to it
      }
    }
  }

  // Calculate scale to match target dimensions
  const boundingInfo = model.boundingInfo;
  const modelSize = boundingInfo.boundingBox.extendSize.scale(2);

  // Convert target dimensions from cm to meters
  const targetWidth = targetDimensions.width * CM_TO_M;
  const targetHeight = targetDimensions.height * CM_TO_M;
  const targetDepth = targetDimensions.depth * CM_TO_M;

  // Calculate scale factors
  const scaleX = targetWidth / Math.max(modelSize.x, 0.001);
  const scaleY = targetHeight / Math.max(modelSize.y, 0.001);
  const scaleZ = targetDepth / Math.max(modelSize.z, 0.001);

  // Use uniform scale (smallest to maintain proportions) or non-uniform
  // For now, use non-uniform to match exact dimensions
  instanceRoot.scaling = new BABYLON.Vector3(scaleX, scaleY, scaleZ);

  // Position (convert from cm to meters, Y is up in Babylon)
  instanceRoot.position = new BABYLON.Vector3(
    position.x * CM_TO_M,
    (targetDimensions.height / 2 + position.z) * CM_TO_M, // Center vertically + elevation
    position.y * CM_TO_M
  );

  // Rotation (Y-axis)
  instanceRoot.rotation.y = (rotation * Math.PI) / 180;

  return instanceRoot;
}

/**
 * Dispose a model instance
 */
export function disposeModelInstance(volumeId: string, scene: BABYLON.Scene): void {
  const instanceRoot = scene.getTransformNodeByName(`glb-instance-${volumeId}`);
  if (instanceRoot) {
    instanceRoot.dispose(false, true); // Dispose children too
  }
}

/**
 * Clear the template cache (e.g., when scene is disposed)
 */
export function clearTemplateCache(): void {
  for (const [, cached] of templateCache) {
    cached.model.rootMesh.dispose(false, true);
  }
  templateCache.clear();
  loadingStatus.clear();
}

/**
 * Preload multiple GLB models in parallel
 */
export async function preloadModels(
  glbKeys: string[],
  scene: BABYLON.Scene
): Promise<Map<string, boolean>> {
  const results = new Map<string, boolean>();

  await Promise.all(
    glbKeys.map(async (key) => {
      const model = await loadGlbModel(key, scene);
      results.set(key, model !== null);
    })
  );

  return results;
}

// =============================================================================
// Internal Functions
// =============================================================================

async function loadModelInternal(
  glbKey: string,
  scene: BABYLON.Scene
): Promise<LoadedModel | null> {
  loadingStatus.set(glbKey, { state: "loading" });

  try {
    // Construct URL for the GLB file
    const url = `${API_BASE}/api/uploads/${glbKey}`;

    // Load the GLB file
    const result = await BABYLON.SceneLoader.ImportMeshAsync(
      "", // Load all meshes
      "", // Empty base URL
      url, // Full URL
      scene,
      undefined, // Progress callback
      ".glb" // File extension hint
    );

    if (result.meshes.length === 0) {
      throw new Error("No meshes found in GLB file");
    }

    // Create a root mesh to hold all loaded meshes
    const rootMesh = new BABYLON.Mesh(`glb-template-${glbKey}`, scene);

    // Calculate combined bounding info
    let minVec = new BABYLON.Vector3(Infinity, Infinity, Infinity);
    let maxVec = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity);

    for (const mesh of result.meshes) {
      if (mesh instanceof BABYLON.Mesh) {
        // Parent to root and disable (template is hidden)
        mesh.setParent(rootMesh);
        mesh.setEnabled(false);

        // Update bounding info
        const meshBounds = mesh.getBoundingInfo();
        const worldMin = meshBounds.boundingBox.minimumWorld;
        const worldMax = meshBounds.boundingBox.maximumWorld;

        minVec = BABYLON.Vector3.Minimize(minVec, worldMin);
        maxVec = BABYLON.Vector3.Maximize(maxVec, worldMax);
      }
    }

    // Hide the template root
    rootMesh.setEnabled(false);

    const boundingInfo = new BABYLON.BoundingInfo(minVec, maxVec);

    const loadedModel: LoadedModel = {
      rootMesh,
      meshes: result.meshes,
      boundingInfo,
    };

    // Cache the template
    templateCache.set(glbKey, {
      model: loadedModel,
      loadedAt: Date.now(),
    });

    loadingStatus.set(glbKey, { state: "loaded" });
    console.log(`[GLB Loader] Loaded model: ${glbKey}`);

    return loadedModel;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error(`[GLB Loader] Failed to load model ${glbKey}:`, errorMessage);
    loadingStatus.set(glbKey, { state: "error", error: errorMessage });
    return null;
  }
}
